/**
 * VBQuantityPresetsCard
 *
 * Admin editor for the customer-facing "How many copies?" quantity
 * presets on the Visual Builder Guided Setup wizard. One editor per
 * product family (vinyl / cd / dvd / bluray) — the parent passes the
 * currently-selected family, we fetch that family's presets on mount
 * and whenever the family changes.
 *
 * Data model (backend /api/visual-builder/quantity-presets/{family}):
 *   { presets: number[], min_qty: number, max_qty: number,
 *     custom_enabled: boolean }
 *
 * UI:
 *   - Chips for each preset with an "×" to remove.
 *   - Text field + "Add" button to insert a new preset (auto-sorted).
 *   - min_qty / max_qty inputs used for the custom-quantity field on
 *     the customer wizard AND for validation.
 *   - A toggle for whether the customer can type a custom quantity.
 *   - Save button PUTs the new config; a "Reset to defaults" link
 *     clears the local edits and re-fetches from the server.
 */
import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { X, Save, RotateCcw, Hash } from 'lucide-react';
import { API_BASE } from '../lib/apiBase';

const API_URL = API_BASE || '';

export function VBQuantityPresetsCard({ productFamily = 'vinyl' }) {
    const [presets, setPresets] = useState([]);
    const [minQty, setMinQty] = useState(50);
    const [maxQty, setMaxQty] = useState(10000);
    const [customEnabled, setCustomEnabled] = useState(true);
    const [newQty, setNewQty] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);

    const familyLabel = {
        vinyl: 'Vinyl', cd: 'CD', dvd: 'DVD', bluray: 'Blu-ray',
    }[productFamily] || productFamily;

    const load = useCallback(async ({ force = false } = {}) => {
        setLoading(true);
        try {
            const res = await fetch(
                `${API_URL}/api/visual-builder/quantity-presets/${encodeURIComponent(productFamily)}`,
            );
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            // Only clobber local state when NOT dirty (initial mount /
            // family switch) OR when the caller explicitly asked for a
            // force reload (the "Reload" button). This prevents a slow
            // GET that resolves after the admin has already started
            // typing from wiping their edits.
            setDirty((currentDirty) => {
                if (!currentDirty || force) {
                    setPresets(Array.isArray(data.presets) ? data.presets.slice().sort((a, b) => a - b) : []);
                    setMinQty(Number(data.min_qty) || 50);
                    setMaxQty(Number(data.max_qty) || 10000);
                    setCustomEnabled(data.custom_enabled !== false);
                    return false;
                }
                return currentDirty;
            });
        } catch (e) {
            toast.error(`Failed to load quantity presets: ${e.message}`);
        } finally {
            setLoading(false);
        }
    }, [productFamily]);

    useEffect(() => {
        load();
    }, [load]);

    const addPreset = () => {
        const n = parseInt(newQty, 10);
        if (!n || n <= 0) {
            toast.error('Enter a positive whole number');
            return;
        }
        if (presets.includes(n)) {
            toast.error(`${n} is already in the list`);
            return;
        }
        setPresets((prev) => [...prev, n].sort((a, b) => a - b));
        setNewQty('');
        setDirty(true);
    };

    const removePreset = (qty) => {
        if (presets.length <= 1) {
            toast.error('At least one preset is required');
            return;
        }
        setPresets((prev) => prev.filter((q) => q !== qty));
        setDirty(true);
    };

    const save = async () => {
        if (presets.length === 0) {
            toast.error('At least one preset is required');
            return;
        }
        if (minQty > maxQty) {
            toast.error('Min must be less than or equal to Max');
            return;
        }
        const outOfRange = presets.filter((q) => q < minQty || q > maxQty);
        if (outOfRange.length > 0) {
            toast.error(`Presets outside [${minQty}, ${maxQty}]: ${outOfRange.join(', ')}`);
            return;
        }
        setSaving(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(
                `${API_URL}/api/visual-builder/admin/quantity-presets/${encodeURIComponent(productFamily)}`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        presets,
                        min_qty: Number(minQty),
                        max_qty: Number(maxQty),
                        custom_enabled: !!customEnabled,
                    }),
                },
            );
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || `HTTP ${res.status}`);
            }
            toast.success(`${familyLabel} quantity presets saved`);
            setDirty(false);
            // No need to re-fetch — the PUT response reflects the
            // authoritative saved state and we already set state above
            // via the optimistic edits. Skipping the load avoids a
            // race where a slow GET resolves after the admin starts a
            // fresh edit.
        } catch (e) {
            toast.error(`Save failed: ${e.message}`);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card data-testid="vb-quantity-presets-card" className="border-purple-100">
            <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <CardTitle className="text-base flex items-center gap-2">
                            <Hash className="h-4 w-4 text-purple-500" />
                            Quantity presets — {familyLabel}
                        </CardTitle>
                        <CardDescription className="mt-0.5">
                            Choose which quantity buttons customers see on the Guided Setup wizard&apos;s &ldquo;How many copies?&rdquo; step.
                        </CardDescription>
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => load({ force: true })}
                        disabled={loading || saving}
                        data-testid="vb-qty-presets-reload"
                    >
                        <RotateCcw className="h-4 w-4 mr-1" />
                        Reload
                    </Button>
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                <div>
                    <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Preset quantities</Label>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                        {presets.length === 0 && !loading && (
                            <span className="text-sm text-slate-400 italic">No presets — add at least one below</span>
                        )}
                        {presets.map((qty) => (
                            <span
                                key={qty}
                                className="inline-flex items-center gap-1 px-3 py-1 rounded-full border border-purple-200 bg-purple-50 text-purple-800 text-sm font-medium"
                                data-testid={`vb-qty-preset-chip-${qty}`}
                            >
                                {qty}
                                <button
                                    type="button"
                                    onClick={() => removePreset(qty)}
                                    className="p-0.5 rounded hover:bg-purple-100 text-purple-500 hover:text-red-500 transition-colors"
                                    aria-label={`Remove ${qty}`}
                                    data-testid={`vb-qty-preset-remove-${qty}`}
                                >
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            </span>
                        ))}
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                        <Input
                            type="number"
                            value={newQty}
                            onChange={(e) => setNewQty(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addPreset(); } }}
                            placeholder="Add quantity, e.g. 750"
                            className="w-44"
                            min="1"
                            data-testid="vb-qty-preset-new-input"
                        />
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={addPreset}
                            disabled={!newQty}
                            data-testid="vb-qty-preset-add-btn"
                        >
                            Add
                        </Button>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <Label htmlFor="vb-qty-min" className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                            Min quantity (custom)
                        </Label>
                        <Input
                            id="vb-qty-min"
                            type="number"
                            value={minQty}
                            onChange={(e) => { setMinQty(parseInt(e.target.value, 10) || 0); setDirty(true); }}
                            className="mt-1"
                            min="1"
                            data-testid="vb-qty-preset-min-input"
                        />
                    </div>
                    <div>
                        <Label htmlFor="vb-qty-max" className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                            Max quantity (custom)
                        </Label>
                        <Input
                            id="vb-qty-max"
                            type="number"
                            value={maxQty}
                            onChange={(e) => { setMaxQty(parseInt(e.target.value, 10) || 0); setDirty(true); }}
                            className="mt-1"
                            min="1"
                            data-testid="vb-qty-preset-max-input"
                        />
                    </div>
                </div>

                <div className="flex items-center justify-between pt-1">
                    <div className="flex items-center gap-3">
                        <Switch
                            id="vb-qty-custom-enabled"
                            checked={customEnabled}
                            onCheckedChange={(v) => { setCustomEnabled(v); setDirty(true); }}
                            data-testid="vb-qty-preset-custom-toggle"
                        />
                        <Label htmlFor="vb-qty-custom-enabled" className="text-sm text-slate-700 cursor-pointer">
                            Allow customers to enter a custom quantity
                        </Label>
                    </div>
                    <Button
                        onClick={save}
                        disabled={saving || loading || !dirty}
                        data-testid="vb-qty-preset-save-btn"
                    >
                        <Save className="h-4 w-4 mr-1" />
                        {saving ? 'Saving…' : 'Save presets'}
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}

export default VBQuantityPresetsCard;
